import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SetStateAction } from 'react';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useTableSearch } from '@/hooks/ui/table/useTableSearch';
import { rolesService } from '@/api/domains/authorization';
import type { RoleItem, RoleFilters } from '@/api/domains/authorization/roles.types';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { Text } from '@/components/ui/text/Text';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';


export const useRoles = () => {
  const { t: translate } = useTranslation();
  const { openSidePanel, closeSidePanel, showNotification, showApiError } = useUI();
  const { canEdit } = usePagePermissions();

  const [filters, internalSetFilters] = useState<RoleFilters>({
    isSystem: undefined,
    isEnabled: undefined,
  });

  // Server-side list state
  const [rows, setRows] = useState<RoleItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { debouncedSearchText, setSearchText } = useTableSearch({
    onDebouncedChange: () => setPage(1),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  // Cambiar filtros siempre vuelve a la primera página, sin importar en cuál se esté parado
  const setFilters = useCallback((updater: SetStateAction<RoleFilters>) => {
    internalSetFilters(updater);
    setPage(1);
  }, []);

  const fetchList = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await rolesService.getRoles({
        page,
        pageSize,
        isSystem: filters.isSystem,
        isEnabled: filters.isEnabled,
        searchText: debouncedSearchText || undefined,
        orderBy: sortKey || undefined,
        orderDirection: sortDirection ? sortDirection.toUpperCase() : undefined,
      });

      if (response.success && response.data) {
        const data = response.data.data || [];

        setRows(data);
        setTotalRecords(response.data.pagination?.totalRecords || 0);
      } else {
        showApiError(response);
      }
    } catch {
      showNotification('error', translate('common:errors.unexpected'));
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    pageSize,
    debouncedSearchText,
    filters,
    sortKey,
    sortDirection,
    showApiError,
    showNotification,
    translate,
  ]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // Escuchar evento de éxito del formulario para refrescar y cerrar
  useEffect(() => {
    const handleRoleFormSuccess = () => {
      void fetchList();
      closeSidePanel();
    };

    window.addEventListener('role-form-success', handleRoleFormSuccess);

    return () => {
      window.removeEventListener('role-form-success', handleRoleFormSuccess);
    };
  }, [fetchList, closeSidePanel]);

  // Actions
  const executeToggleStatus = useCallback(async (roleId: string, newStatus: boolean) => {
    // Optimistic update
    setTogglingIds((previous) => new Set(previous).add(roleId));
    setRows((previous) => previous.map((role) =>
      role.id === roleId ? { ...role, isEnabled: newStatus } : role,
    ));

    const response = await rolesService.toggleRoleStatus(roleId, newStatus);

    if (response.success) {
      showNotification(
        'success',
        translate('authorization:roles.messages.statusChangeSuccess'),
      );
      void fetchList();
    } else {
      // Rollback
      setRows((previous) => previous.map((role) =>
        role.id === roleId ? { ...role, isEnabled: !newStatus } : role,
      ));
      showApiError(response);
    }

    setTogglingIds((previous) => {
      const next = new Set(previous);
      next.delete(roleId);
      return next;
    });

    return response;
  }, [showNotification, showApiError, translate, fetchList]);

  const openFormPanel = useCallback((roleId?: string) => {
    openSidePanel({
      component: 'RoleFormPanel',
      title: roleId 
        ? translate('authorization:roles.editRole')
        : translate('authorization:roles.newRole'),
      props: {
        roleId,
        mode: 'edit',
      },
    });
  }, [openSidePanel, translate]);

  const openViewPanel = useCallback((roleId: string) => {
    openSidePanel({
      component: 'RoleFormPanel',
      title: translate('authorization:roles.viewRole'),
      props: {
        roleId,
        mode: 'view',
      },
    });
  }, [openSidePanel, translate]);

  // Sort handler
  const sortKeyMap: Record<string, string> = useMemo(() => ({
    isSystem: 'type',
    isEnabled: 'status',
  }), []);

  const onSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(sortKeyMap[key] ?? key);
    setSortDirection(direction);
  }, [sortKeyMap]);

  // Columns
  const columns: TableColumn<RoleItem>[] = useMemo(() => [
    {
      key: 'id',
      header: translate('authorization:roles.fields.id'),
      width: '100px',
      sortable: true,
    },
    {
      key: 'name',
      header: translate('authorization:roles.fields.role'),
      width: '200px',
      sortable: true,
    },
    {
      key: 'description',
      header: translate('authorization:roles.fields.description'),
      width: '300px',
      sortable: true,
    },
    {
      key: 'isSystem',
      header: translate('authorization:roles.fields.isSystem'),
      width: '150px',
      sortable: true,
      render: (role) => (
        <Text variant="subheader-regular" as="span">
          {role.type ?? '-'}
        </Text>
      ),
    },
    {
      key: 'isEnabled',
      header: translate('authorization:roles.fields.status'),
      width: '120px',
      sortable: true,
      render: (role) => (
        <Text variant="subheader-regular" as="span">
          {role.status ?? '-'}
        </Text>
      ),
    },
  ], [translate]);

  // Actions Menu
  const actions: TableAction<RoleItem>[] = useMemo(() => [
    {
      id: 'view',
      icon: 'eye',
      label: translate('common:tableActions.detail'),
      onClick: (role) => openViewPanel(role.id),
    },
    {
      id: 'edit',
      icon: 'pen',
      label: translate('common:tableActions.edit'),
      hidden: () => !canEdit,
      onClick: (role) => openFormPanel(role.id),
    },
    {
      id: 'disable',
      icon: 'close',
      label: translate('common:tableActions.disable'),
      hidden: (role) => !role.isEnabled,
      onClick: (role) => { void executeToggleStatus(role.id, false); },
      variant: 'danger',
      disabled: (role) => togglingIds.has(role.id),
      isLoading: (role) => togglingIds.has(role.id),
    },
    {
      id: 'enable',
      icon: 'check',
      label: translate('common:tableActions.enable'),
      hidden: (role) => role.isEnabled,
      onClick: (role) => { void executeToggleStatus(role.id, true); },
      disabled: (role) => togglingIds.has(role.id),
      isLoading: (role) => togglingIds.has(role.id),
    },
  ], [translate, openFormPanel, openViewPanel, executeToggleStatus, togglingIds, canEdit]);

  const statusOptions = useMemo(() => [
    { value: 'all', label: translate('authorization:roles.status.all') },
    { value: 'active', label: translate('authorization:roles.status.active') },
    { value: 'inactive', label: translate('authorization:roles.status.inactive') },
  ], [translate]);

  const typeOptions = useMemo(() => [
    { value: 'all', label: translate('authorization:roles.types.all') },
    { value: 'system', label: translate('authorization:roles.types.system') },
    { value: 'custom', label: translate('authorization:roles.types.custom') },
  ], [translate]);

  const statusValue = filters.isEnabled === undefined ? 'all' : (filters.isEnabled ? 'active' : 'inactive');
  const typeValue = filters.isSystem === undefined ? 'all' : (filters.isSystem ? 'system' : 'custom');

  const handleStatusChange = useCallback((value: string | string[]) => {
    const single = Array.isArray(value) ? value[0] : value;
    let isEnabled: RoleFilters['isEnabled'] = undefined;
    if (single === 'active') isEnabled = true;
    if (single === 'inactive') isEnabled = false;
    setFilters((prev) => ({ ...prev, isEnabled }));
  }, [setFilters]);

  const handleTypeChange = useCallback((value: string | string[]) => {
    const single = Array.isArray(value) ? value[0] : value;
    let isSystem: RoleFilters['isSystem'] = undefined;
    if (single === 'system') isSystem = true;
    if (single === 'custom') isSystem = false;
    setFilters((prev) => ({ ...prev, isSystem }));
  }, [setFilters]);

  return {
    filters,
    setFilters,
    data: rows,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch: setSearchText,
    onSortChange,
    columns,
    actions,
    openNewRequestPanel: () => openFormPanel(),
    executeToggleStatus,
    statusOptions,
    typeOptions,
    statusValue,
    typeValue,
    handleStatusChange,
    handleTypeChange,
  };
};
